import { Stack, Typography } from "@mui/material";
import { Button } from "@/components/elements/Button";
import { AppRoutes } from "@/routes/config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FC, useState } from "react";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useDialog } from "@/hooks/useDialog";
import { getActiveJobsByCompany, setCompanyArchive } from "@/api/services/main/company";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { getDynamicRoute } from "@/utils/url";
import { showError } from "@/utils/error";
import { COMPANY_PAGE_STORAGE_KEY } from "@/features/company/constants/common";
import { TGetCompanyActiveJobsDto } from "@/api/services/main/company/dtos/get-company.dto";
import { CompanyWarningListModal } from "@/features/company/components/CompanyWarningListModal";

type TCompanyFooterContainerProps = {
  children?: React.ReactNode;
  isShowSetArchiveButton?: boolean;
  isArchive?: boolean | null | undefined;
};
export const CompanyFooterContainer: FC<TCompanyFooterContainerProps> = ({ children, isShowSetArchiveButton = false, isArchive }) => {
  const navigate = useNavigate();
  const confirmModal = useDialog();
  const { companyId } = useParams();
  const [isSubmit, setIsSubmit] = useState(false);
  const location = useLocation();
  const [activeJobs, setActiveJobs] = useState<TGetCompanyActiveJobsDto[]>([]);
  const hasActiveJobModal = useDialog();

  const getBackRoute = () => {
    if (companyId) {
      const { pathname } = location;
      if (pathname.search("edit") > 0) {
        return getDynamicRoute(AppRoutes.companyDetail, { companyId });
      }
    }
    const savedPage = sessionStorage.getItem(COMPANY_PAGE_STORAGE_KEY) || "1";
    return `${AppRoutes.companyList}?page=${savedPage}`;
  };

  const backRoute = getBackRoute();

  const onConfirmSetCompanyArchive = async (archiveStatus: boolean) => {
    if (!companyId) return;
    setIsSubmit(true);
    try {
      const { data: activeJobs } = await getActiveJobsByCompany(companyId);
      if (activeJobs?.jobs && activeJobs.jobs?.length > 0) {
        confirmModal.cancel();
        setActiveJobs(activeJobs.jobs);
        hasActiveJobModal.show();
      } else {
        await handleSetArchiveCompany(archiveStatus);
      }
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmit(false);
    }
  };

  const handleSetArchiveCompany = async (archiveStatus: boolean) => {
    try {
      await setCompanyArchive(Number(companyId), archiveStatus);
      toast.success(API_SUCCESS_MESSAGE);
      navigate(AppRoutes.companyList);
    } catch (e) {
      showError(e);
    }
  };

  const ButtonSetArchive = () => {
    if (!isShowSetArchiveButton) return <></>;
    if (isArchive) {
      return (
        <Button variant="ghost" size="sm" disabled={isSubmit} onClick={() => handleSetArchiveCompany(false)}>
          アーカイブから戻す
        </Button>
      );
    }
    return (
      <Button variant="ghost" size="sm" disabled={isSubmit} onClick={() => confirmModal.show()}>
        アーカイブ
      </Button>
    );
  };

  return (
    <FooterContainer>
      <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={2}>
          <Button size="sm" variant="ghost" onClick={() => navigate(backRoute)}>
            戻る
          </Button>
          <ButtonSetArchive />
        </Stack>
        {children}
      </Stack>
      <ConfirmSaveModal open={confirmModal.open} onOk={() => onConfirmSetCompanyArchive(true)} onClose={confirmModal.cancel}>
        <Typography>企業をアーカイブすると新しい求人を作成できません。アーカイブしてもよろしいですか？</Typography>
      </ConfirmSaveModal>
      <CompanyWarningListModal
        label="以下の募集中の求人に設定されているため、アーカイブできません。"
        mode="active-job"
        open={hasActiveJobModal.open}
        onClose={hasActiveJobModal.cancel}
        list={activeJobs}
      />
    </FooterContainer>
  );
};
